import React, { memo, useMemo, lazy, Suspense, } from 'react';
import PropTypes from 'prop-types';
import { Paper, Box, Avatar, Divider } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { useUI } from '../../context/UIContext';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
const ChatMessageContent = React.lazy(() => import('./message/ChatMessageContent'));
import ProcessUpdates from '../agentStatus/ProcessUpdates';
import AudioButton from './message/AudioButton';
import EffectContainer from './effects/EffectContainer';

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$senderType',
})(({ theme, $senderType }) => {
  let justifyContent = 'flex-start';
  if ($senderType === 'user') {
    justifyContent = 'flex-end';
  } else if ($senderType === 'app') {
    justifyContent = 'center';
  }
  return {
    display: 'flex',
    justifyContent,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0, 1),
    alignItems: 'flex-end',
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(0),
    },
  };
});

const MessageContent = styled(Paper, {
  shouldForwardProp: (prop) => prop !== '$senderType' && prop !== '$isLastAgentMessage' && prop !== '$variant',
})(({ theme, $senderType, $isLastAgentMessage, $variant }) => {
  const baseStyles = {
    padding: theme.spacing(1, 2),
    borderRadius: theme.shape.borderRadius * 1.5,
    position: 'relative',
    maxWidth: '80%',
    display: 'flex',
    flexDirection: $senderType === 'app' ? 'row' : 'column',
    [theme.breakpoints.down('md')]: {
      maxWidth: '95%',
    },
    '& .markdown-content': {
      '& p': {
        margin: 0,
      },
      '& p:not(:last-child)': {
        marginBottom: theme.spacing(1),
      }
    }
  };

  if ($variant === 'card') {
    return {
      ...baseStyles,
      backgroundColor: 'transparent !important',
      backgroundImage: 'none !important',
      boxShadow: 'none !important',
      border: 'none !important',
      padding: 0,
      width: '100%',
      maxWidth: '100%',
      justifyContent: 'center',
    };
  }

  if ($senderType === 'user') {
    return {
      ...baseStyles,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderBottomRightRadius: theme.spacing(0.5),
    };
  } else if ($senderType === 'app') {
    return {
      ...baseStyles,
      backgroundColor: alpha(theme.palette.info.main, 0.1),
      color: theme.palette.mode === 'dark' ? theme.palette.info.light : theme.palette.info.dark,
      border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
      textAlign: 'center',
      maxWidth: '90%',
      boxShadow: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2),
    };
  } else {
    return {
      ...baseStyles,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      borderBottomLeftRadius: theme.spacing(0.5),
      ...($isLastAgentMessage && {
        transform: 'translateY(-2px)',
        boxShadow: `0 10px 30px ${alpha(theme.palette.primary.main, 0.3)}, 0 0 20px ${alpha(theme.palette.primary.main, 0.2)}`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.6)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
          opacity: 1,
        },
      }),
    };
  }
});

const AvatarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isUser',
})(({ theme, $isUser }) => ({
  marginRight: $isUser ? 0 : theme.spacing(1),
  marginLeft: $isUser ? theme.spacing(1) : 0,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const ChatMessage = ({ message, currentlyPlayingId, processData, effects, isLastAgentMessage }) => {
  const { text, sender, id: messageId, isAnimated, variant } = message;
  const isUser = sender === 'user';
  const { isMobile } = useUI();
  const { agentChatAvatar } = useConfig();
  const { user } = useAuth();

  const isProcessOnlyPlaceholder = sender === 'system' && text === '...' && (processData || (effects && effects.length > 0));
  const showContent = !isProcessOnlyPlaceholder || isAnimated;
  const isCard = variant === 'card';

  const isThisMessagePlaying = useMemo(() => {
    if (!currentlyPlayingId || !messageId) return false;
    return currentlyPlayingId.startsWith(messageId);
  }, [currentlyPlayingId, messageId]);

  const showAudioButton = !isUser && !isMobile && text && typeof text === 'string' && text.trim() !== '' && text.trim() !== '...' && !isCard;

  const contentBlock = showContent ? (
    <Box sx={{ width: '100%' }}>
      <React.Suspense fallback={<span>...</span>}>
        <ChatMessageContent message={message} isMobile={isMobile} messageId={messageId} />
      </React.Suspense>
    </Box>
  ) : null;

  const statusBlock = (
    <>
      {processData && (
        <Box sx={{ pr: showAudioButton ? 4 : 0, mt: isCard ? 1.5 : -1.5 }}>
          <ProcessUpdates processTree={processData.processTree} />
        </Box>
      )}
      {effects && effects.length > 0 && (
        <EffectContainer effects={effects} />
      )}
    </>
  );

  return (
    <MessageContainer $senderType={sender} id={`chat-message-${messageId}`}>
      {!isUser && sender !== 'app' && (
        <AvatarWrapper>
          {agentChatAvatar}
        </AvatarWrapper>
      )}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: sender === 'user' ? 'flex-end' : (sender === 'app' ? 'center' : 'flex-start'),
        maxWidth: '100%',
        width: sender === 'app' ? '100%' : 'auto',
      }}>
        <MessageContent
          $senderType={sender}
          $isLastAgentMessage={isLastAgentMessage}
          $variant={variant}
          elevation={isCard ? 0 : 1}
          sx={{
            transition: 'background-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease',
            backgroundColor: isThisMessagePlaying && !isCard
              ? (theme) => alpha(theme.palette.secondary.main, 0.2)
              : (isUser ? (theme) => theme.palette.primary.main : undefined),
          }}
        >
          {showAudioButton && (
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
              <AudioButton text={text} messageId={messageId} />
            </Box>
          )}

          {isCard ? (
            <>
              {contentBlock}
              {statusBlock}
            </>
          ) : (
            <>
              {statusBlock}
              {(processData || (effects && effects.length > 0)) && showContent && text !== '...' && (
                <Divider sx={{ mb: 0.25, mt: 1, borderColor: isUser ? 'rgba(255, 255, 255, 0.2)' : 'divider' }} />
              )}
              {contentBlock}
            </>
          )}
        </MessageContent>
      </Box>
      {isUser && (
        <AvatarWrapper>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            {user?.username?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
        </AvatarWrapper>
      )}
    </MessageContainer>
  );
};

ChatMessage.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    sender: PropTypes.oneOf(['user', 'system', 'app']).isRequired,
    timestamp: PropTypes.string,
    originalText: PropTypes.string,
    isTemporary: PropTypes.bool,
    isAnimated: PropTypes.bool,
    variant: PropTypes.string,
    scrollMode: PropTypes.string,
  }).isRequired,
  currentlyPlayingId: PropTypes.string,
  processData: PropTypes.object,
  effects: PropTypes.array,
  isLastAgentMessage: PropTypes.bool,
};

export default memo(ChatMessage);